import * as React from "react";
import { toast } from "sonner";

import type {
	MaterialOption,
	PurchaseFormValues,
	PurchaseResource,
	SupplierOption,
} from "../types";
import { useUpdatePurchaseMutation } from "../hooks/useUpdatePurchaseMutation";
import { PurchasesForm } from "./PurchasesForm";
import {
	DEFAULT_VALUES,
	buildPurchasePayload,
	usePurchasesForm,
} from "./usePurchasesForm";

type PurchasesEditFormControllerProps = {
	open: boolean;
	defaultValues: PurchaseFormValues;
	disableMaterial: boolean;
	supplierOption: SupplierOption | null;
	materialOption: MaterialOption | null;
	onRequestNewSupplier: () => void;
	onRequestNewMaterial: () => void;
	onClose: () => void;
	onCancel: () => void;
	onUpdated: (purchase: PurchaseResource) => void;
};

export const PurchasesEditFormController: React.FC<
	PurchasesEditFormControllerProps
> = ({
	open,
	defaultValues,
	disableMaterial,
	supplierOption,
	materialOption,
	onRequestNewSupplier,
	onRequestNewMaterial,
	onClose,
	onCancel,
	onUpdated,
}) => {
	const { form, resetToInitialValues, querySuppliers, queryMaterials } =
		usePurchasesForm({ defaultValues });

	const { mutateAsync: updatePurchase, isPending } =
		useUpdatePurchaseMutation();

	const handleSubmit = form.handleSubmit(async (values) => {
		try {
			const payload = {
				...DEFAULT_VALUES,
				...values,
			};

			if (!payload.id) {
				toast.error("Não foi possível identificar a compra selecionada.");
				return;
			}

			const purchase = await updatePurchase({
				...buildPurchasePayload(payload),
				id: payload.id,
			});

			toast.success("Compra atualizada.");
			onUpdated(purchase);

			onClose();
		} catch (error) {
			toast.error("Não foi possível guardar a compra. Tente novamente.");
		}
	});

	const handleCancel = () => {
		onCancel();
		resetToInitialValues();
	};

	return (
		<PurchasesForm
			dialogTitle="Editar compra"
			open={open}
			form={form}
			isSubmitting={isPending}
			supplierOption={supplierOption}
			onSupplierQuery={querySuppliers}
			onRequestNewSupplier={onRequestNewSupplier}
			materialOption={materialOption}
			onMaterialQuery={queryMaterials}
			onRequestNewMaterial={onRequestNewMaterial}
			disableMaterial={disableMaterial}
			onSubmit={handleSubmit}
			onCancel={handleCancel}
			onClose={onClose}
		/>
	);
};
